"use client";
import MusicNoteIcon from "@mui/icons-material/MusicNote";
import { useEffect, useState } from "react";
import Link from "~/components/Link";
import { trackEvent } from "~/connectors/analytics.connector";
import { AnalyticsEvents } from "~/connectors/analyticsEvents";

import Box from "@mui/material/Box";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import Typography from "@mui/material/Typography";

const SESSION_STORAGE_KEY = "exitIntentShown";

export default function ExitIntentPopup() {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    // Check if popup was already shown in this session
    const wasShown = sessionStorage.getItem(SESSION_STORAGE_KEY);
    if (wasShown) {
      return;
    }

    let hasShownPopup = false;

    const handleMouseLeave = (e: MouseEvent) => {
      // Only trigger if mouse is leaving from the top of the page
      // and we haven't shown the popup yet
      if (e.clientY <= 0 && !hasShownPopup) {
        hasShownPopup = true;
        setOpen(true);
        sessionStorage.setItem(SESSION_STORAGE_KEY, "true");

        // Track popup shown event
        trackEvent(AnalyticsEvents.VERSURI_EXIT_INTENT_POPUP_SHOWN);
      }
    };

    // Add slight delay before attaching listener to avoid immediate trigger
    const timeoutId = setTimeout(() => {
      document.addEventListener("mouseleave", handleMouseLeave);
    }, 1000);

    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener("mouseleave", handleMouseLeave);
    };
  }, []);

  const handleClose = () => {
    // Track dialog dismissal (closed without CTA click)
    trackEvent(AnalyticsEvents.VERSURI_EXIT_INTENT_POPUP_DISMISSED);
    setOpen(false);
  };

  const handleCtaClick = () => {
    // Don't track dismissal event when CTA is clicked
    // The Link component will track VERSURI_EXIT_INTENT_POPUP_CTA_CLICK
    setOpen(false);
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      slotProps={{
        paper: {
          sx: {
            borderRadius: 4,
            overflow: "hidden",
            background: `linear-gradient(135deg, var(--mui-palette-primary-light) 0%, var(--mui-palette-primary-main) 50%, var(--mui-palette-secondary-main) 100%)`,
            position: "relative",
          },
        },
      }}
    >
      <DialogContent
        sx={{
          p: { xs: 3, sm: 5 },
          bgcolor: "background.paper",
          m: 2,
          borderRadius: 5,
          textAlign: "center",
        }}
      >
        {/* Icon decoration */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            gap: 1,
            mb: 3,
          }}
        >
          <Box
            sx={{
              bgcolor: "primary.main",
              borderRadius: "50%",
              width: 60,
              height: 60,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              animation: "bounce 1s ease-in-out infinite",
              "@keyframes bounce": {
                "0%, 100%": {
                  transform: "translateY(0)",
                },
                "50%": {
                  transform: "translateY(-10px)",
                },
              },
            }}
          >
            <MusicNoteIcon sx={{ fontSize: 32, color: "white" }} />
          </Box>
        </Box>

        {/* Main message */}
        <Typography
          variant="h4"
          sx={{
            fontFamily: "var(--font-headline)",
            fontSize: { xs: "1.5rem", sm: "2rem" },
            fontWeight: 700,
            color: "primary.main",
            mb: 2,
            lineHeight: 1.3,
          }}
        >
          Nu pleca încă!
        </Typography>

        <Typography
          variant="body1"
          sx={{
            fontFamily: "var(--font-body)",
            fontSize: { xs: "1.1rem", sm: "1.25rem" },
            color: "text.primary",
            mb: 4,
            lineHeight: 1.6,
          }}
        >
          Pune povestea puiului tău pe note. Vezi cum poți crea o melodie unică,
          în care el este personajul principal.
        </Typography>

        {/* CTA Button */}
        <Link
          href="/amintiri"
          button
          variant="contained"
          onClick={handleCtaClick}
          trackable={AnalyticsEvents.VERSURI_EXIT_INTENT_POPUP_CTA_CLICK}
          sx={{
            fontSize: { xs: "1rem", sm: "1.2rem" },
            px: 4,
            py: 2,
            fontFamily: "var(--font-body)",
            fontWeight: 600,
            boxShadow: 3,
            "&:hover": {
              boxShadow: 6,
              transform: "translateY(-2px)",
            },
            transition: "all 0.3s ease",
          }}
        >
          Vezi exemple aici
        </Link>
      </DialogContent>
    </Dialog>
  );
}
